import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MainLayout from "../../../components/layouts/MainLayout/MainLayout";
import { DataContext } from "../../../context/DataContext";
import { getNameInitials } from "../../../utils";

const Profile = () => {

    const [userInfo, setUserInfo] = useState({})
    const [userInitials, setUserInitials] = useState('')
    const { userData } = useContext(DataContext)
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [originalForm, setOriginalForm] = useState(null);

    /******************************************************************
    * Form data for profile user
    *******************************************************************/
    const [formData, setFormData] = useState({
        name: '',
        user_email: '',
        gender: '',
        user_age: '',
        user_weight: '',
        user_height: '',
        user_complexion: '',
        user_religion: '',
        smoke: '',
        drink_alcohol: '',
        does_exercise: '',
        use_drugs: '',
        medicines: '',
        diseases: '',
        educational_level: '',
        personality: '',
        feelings: '',
        sexual_preference: '',
    });


    /******************************************************************
    * Init page process
    * Get cache data
    * Get profile of database by rest
    *******************************************************************/
    useEffect(() => {
        if (!userData || !userData.attributes) return;

        const { attributes } = userData;
        const initials = getNameInitials(attributes.name || "");
        setUserInfo(attributes);
        setUserInitials(initials);
        setFormData((prev) => ({ ...prev, user_email: attributes.email || "", name: attributes.name || "" }));

        fetch(`https://iu9ozlcvo5.execute-api.us-east-1.amazonaws.com/dev/client/profile?user_email=${attributes.email}`, {
            method: 'GET',
            mode: "cors",
            cache: "no-cache",
            headers: {
                'x-api-key': 'XYHRgAMdCS8jhBf6z8IEL9DPoNPkX4StKmCPtK59',
                'Content-Type': 'application/json'
            },
        })
            .then((response) => response.json())
            .then((data) => {
                setFormData({
                    name: attributes.name || "",
                    user_email: data.body.user_email.S,
                    gender: data.body.gender.S,
                    user_age: data.body.user_age.S.toString(),
                    user_weight: data.body.user_weight.S.toString(),
                    user_height: data.body.user_height.S.toString(),
                    user_complexion: data.body.user_complexion.S,
                    user_religion: data.body.user_religion.S,
                    smoke: data.body.smoke.S,
                    drink_alcohol: data.body.drink_alcohol.S,
                    does_exercise: data.body.does_exercise.S,
                    use_drugs: data.body.use_drugs.S,
                    medicines: data.body.medicines.S,
                    diseases: data.body.diseases.S,
                    educational_level: data.body.educational_level.S,
                    personality: data.body.personality.S,
                    feelings: data.body.feelings.S,
                    sexual_preference: data.body.sexual_preference.S,
                });
                setOriginalForm({
                    name: attributes.name || "",
                    user_email: data.body.user_email.S,
                    gender: data.body.gender.S,
                    user_age: data.body.user_age.S.toString(),
                    user_weight: data.body.user_weight.S.toString(),
                    user_height: data.body.user_height.S.toString(),
                    user_complexion: data.body.user_complexion.S,
                    user_religion: data.body.user_religion.S,
                    smoke: data.body.smoke.S,
                    drink_alcohol: data.body.drink_alcohol.S,
                    does_exercise: data.body.does_exercise.S,
                    use_drugs: data.body.use_drugs.S,
                    medicines: data.body.medicines.S,
                    diseases: data.body.diseases.S,
                    educational_level: data.body.educational_level.S,
                    personality: data.body.personality.S,
                    feelings: data.body.feelings.S,
                    sexual_preference: data.body.sexual_preference.S,
                });

            })
            .catch((error) => {
                console.error('Error al obtener la información del usuario:', error);
            });
    }, [userData])

    
    /******************************************************************
    * Set form data to rest api
    *******************************************************************/
    const handleFormSubmit = (e) => {
        e.preventDefault();
        console.log("formData: ", formData)
        // Enviar los datos del formulario al servicio REST por método POST usando fetch
        fetch('https://iu9ozlcvo5.execute-api.us-east-1.amazonaws.com/dev/client/profile', {
            method: 'POST',
            mode: "cors",
            cache: "no-cache",
            headers: {
                'x-api-key': 'XYHRgAMdCS8jhBf6z8IEL9DPoNPkX4StKmCPtK59',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(formData),
        })
            .then((response) => response.json())
            .then((data) => {
                // Manejar la respuesta del servicio REST si es necesario
                console.log('Respuesta del servicio:', data);
                // El formulario se envió exitosamente
                setShowSuccessMessage(true); // Muestra el mensaje de éxito
                setEditMode(false);
                setOriginalForm(formData);
            })
            .catch((error) => {
                console.error('Error al enviar el formulario:', error);
            });
    };

    /******************************************************************
    * Update form data variable on change field
    *******************************************************************/
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        console.log(name, value)
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleCancel = () => {
        if (originalForm) {
            setFormData(originalForm);
        }
        setEditMode(false);
    };

    const FieldCard = ({ label, value, suffix }) => (
        <div className="rounded-2xl bg-green-50 border border-green-100 p-3">
            <p className="text-[12px] text-green-700">{label}</p>
            <p className="text-sm md:text-base font-semibold text-green-900 leading-tight">
                {value || '—'} {suffix || ''}
            </p>
        </div>
    );
    const inputClass = (extra = "") =>
        `w-full rounded-lg px-3 py-1.5 text-sm outline-none ${editMode ? "border border-gray-200 bg-white focus:ring-2 focus:ring-emerald-300" : "bg-green-50 border border-green-100 text-green-900"} ${extra}`;

    /******************************************************************
    * Return GUI of form data
    *******************************************************************/
    return (
        <MainLayout>
            {showSuccessMessage && (
                <div className="bg-green-200 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                    <p className="font-bold">¡Actualización exitosa!</p>
                    <p>Tu formulario se ha enviado correctamente.</p>
                </div>
            )}

            <div className="p-5 xl:p-7 pb-10 animate__animated animate__fadeIn gray-app-bg min-h-screen">
                <div className="flex flex-col lg:flex-row gap-6">
                    <aside className="w-full lg:w-1/3">
                        <div className="rounded-2xl border border-green-100 bg-white shadow-sm p-4 space-y-3">
                            <div className="flex items-center gap-3">
                                {userInfo?.picture ? (
                                    <img
                                        src={userInfo.picture}
                                        alt="Avatar"
                                        className="h-12 w-12 rounded-full object-cover border border-green-100"
                                        onError={(e) => { e.currentTarget.src = ""; }}
                                    />
                                ) : (
                                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 text-white flex items-center justify-center text-lg font-semibold">
                                        {userInitials || '??'}
                                    </div>
                                )}
                                <div>
                                    <p className="text-xs text-gray-500">Perfil</p>
                                    <p className="text-base font-semibold text-green-900 leading-tight">{formData.name || 'Sin nombre'}</p>
                                    <p className="text-xs text-gray-600">{userInfo?.email || 'Sin correo'}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <FieldCard label="Edad" value={formData.user_age} suffix="años" />
                                <FieldCard label="Género" value={formData.gender} />
                                <FieldCard label="Peso" value={formData.user_weight} suffix="kg" />
                                <FieldCard label="Altura" value={formData.user_height} suffix="cm" />
                            </div>
                            <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 space-y-2 text-sm text-gray-700">
                                <p className="font-semibold text-green-900 text-sm">Hábitos y salud</p>
                                <div className="flex flex-wrap gap-2 text-xs">
                                    <span className="px-2.5 py-1 rounded-full bg-white border border-green-100 text-green-800">
                                        Ejercicio: {formData.does_exercise || '—'}
                                    </span>
                                    <span className="px-2.5 py-1 rounded-full bg-white border border-green-100 text-green-800">
                                        Alcohol: {formData.drink_alcohol || '—'}
                                    </span>
                                    <span className="px-2.5 py-1 rounded-full bg-white border border-green-100 text-green-800">
                                        Fuma: {formData.smoke || '—'}
                                    </span>
                                </div>
                                <div className="text-xs text-gray-600 leading-snug">
                                    <p>Medicamentos: {formData.medicines || '—'}</p>
                                    <p>Padecimientos: {formData.diseases || '—'}</p>
                                </div>
                            </div>
                            <Link to="/profile/change-password" className="w-full inline-flex gap-2 items-center justify-center bg-blue-500 hover:bg-blue-700 text-white font-semibold h-12 px-4 rounded-lg transition duration-200">
                                Cambiar contraseña <PencilSquareIcon className="w-5 h-5" />
                            </Link>
                        </div>
                    </aside>

                    <div className="flex-1 space-y-6">
                        <div className="rounded-2xl border border-green-100 bg-white shadow-sm p-4">
                            <p className="font-semibold text-green-900 text-center text-sm">
                                La siguiente información nos ayuda a ajustar mejor las respuestas del chatbot
                            </p>
                        </div>

                        <div className="rounded-2xl border border-green-100 bg-white shadow-sm p-4 md:p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">Nombre</h3>
                                        <input
                                            name="name"
                                            id="name"
                                            className={inputClass()}
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">Email</h3>
                                        <input
                                            name="user_email"
                                            id="user_email"
                                            className={inputClass()}
                                            value={formData.user_email}
                                            onChange={handleInputChange}
                                            disabled
                                        />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">Género</h3>
                                        <select
                                            name="gender"
                                            id="gender"
                                            className={inputClass()}
                                            value={formData.gender}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        >
                                            <option value="Hombre">Hombre</option>
                                            <option value="Mujer">Mujer</option>
                                            <option value="Otro">Otro</option>
                                        </select>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">Edad</h3>
                                        <input
                                            type="number"
                                            name="user_age"
                                            id="user_age"
                                            min="14"
                                            max="99"
                                            className={inputClass()}
                                            value={formData.user_age}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">Peso</h3>
                                        <div className="flex items-center">
                                            <input
                                            type="number"
                                            name="user_weight"
                                            id="user_weight"
                                            step="0.1"
                                            min="30"
                                            max="150"
                                            className={inputClass()}
                                            value={formData.user_weight}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        />
                                            <span className="text-gray-500 ml-2">kg</span>
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">Altura</h3>
                                        <div className="flex items-center">
                                            <input
                                            type="number"
                                            name="user_height"
                                            id="user_height"
                                            step="0.01"
                                            min="1"
                                            max="3"
                                            className={inputClass()}
                                            value={formData.user_height}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        />
                                            <span className="text-gray-500 ml-2">mts</span>
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">Complexión</h3>
                                        <select
                                            name="user_complexion"
                                            id="user_complexion"
                                            className={inputClass()}
                                            value={formData.user_complexion}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        >
                                            <option value="delgado">Delgado</option>
                                            <option value="medio">Medio</option>
                                            <option value="robusto">Robusto</option>
                                            <option value="atletico">Atlético</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">Me identifico más con la religión</h3>
                                        <select
                                            name="user_religion"
                                            id="user_religion"
                                            className={inputClass()}
                                            value={formData.user_religion}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        >
                                            <option value="Catolico">Católico</option>
                                            <option value="Cristiano">Cristiano</option>
                                            <option value="Mormon">Mormón</option>
                                            <option value="Judio">Judío</option>
                                            <option value="Protestante">Protestante</option>
                                            <option value="Sin religion">Sin religión</option>
                                            <option value="Espiritualidad">Espiritualidad</option>
                                            <option value="Islam">Islam</option>
                                        </select>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">¿Fumas?</h3>
                                        <select
                                            name="smoke"
                                            id="smoke"
                                            className={inputClass()}
                                            value={formData.smoke}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        >
                                            <option value="Si">Sí</option>
                                            <option value="No">No</option>
                                        </select>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">¿Tomas alcohol?</h3>
                                        <select
                                            name="drink_alcohol"
                                            id="drink_alcohol"
                                            className={inputClass()}
                                            value={formData.drink_alcohol}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        >
                                            <option value="Si">Sí</option>
                                            <option value="No">No</option>
                                        </select>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">¿Haces ejercicio?</h3>
                                        <select
                                            name="does_exercise"
                                            id="does_exercise"
                                            className={inputClass()}
                                            value={formData.does_exercise}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        >
                                            <option value="Si">Sí</option>
                                            <option value="No">No</option>
                                        </select>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">¿Utilizas alguna droga?</h3>
                                        <select
                                            name="use_drugs"
                                            id="use_drugs"
                                            className={inputClass()}
                                            value={formData.use_drugs}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        >
                                            <option value="Si">Sí</option>
                                            <option value="No">No</option>
                                        </select>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">¿Tomas algún medicamento?</h3>
                                        <select
                                            name="medicines"
                                            id="medicines"
                                            className={inputClass()}
                                            value={formData.medicines}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        >
                                            <option value="Si">Sí</option>
                                            <option value="No">No</option>
                                        </select>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">¿Tienes alguna enfermedad?</h3>
                                        <select
                                            name="diseases"
                                            id="diseases"
                                            className={inputClass()}
                                            value={formData.diseases}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        >
                                            <option value="Si">Sí</option>
                                            <option value="No">No</option>
                                        </select>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">Nivel de estudios</h3>
                                        <select
                                            name="educational_level"
                                            id="educational_level"
                                            className={inputClass()}
                                            value={formData.educational_level}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        >
                                            <option value="Primaria">Primaria</option>
                                            <option value="Media Superior">Media Superior</option>
                                            <option value="Licenciatura">Licenciatura</option>
                                            <option value="Maestría">Maestría</option>
                                            <option value="Doctorado">Doctorado</option>
                                        </select>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">¿Cómo consideras tu personalidad?</h3>
                                        <select
                                            name="personality"
                                            id="personality"
                                            className={inputClass()}
                                            value={formData.personality}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        >
                                            <option value="Introvertido">Introvertido</option>
                                            <option value="Extrovertido">Extrovertido</option>
                                            <option value="Tímido">Tímido</option>
                                        </select>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">¿Cómo te sientes normalmente?</h3>
                                        <select
                                            name="feelings"
                                            id="feelings"
                                            className={inputClass()}
                                            value={formData.feelings}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        >
                                            <option value="Feliz">Feliz</option>
                                            <option value="Triste">Triste</option>
                                            <option value="Enfadado">Enfadado</option>
                                            <option value="Sorprendido">Sorprendido</option>
                                            <option value="Frustrado">Frustrado</option>
                                            <option value="Asustado">Asustado</option>
                                            <option value="Nervioso">Nervioso</option>
                                            <option value="Tímido">Tímido</option>
                                            <option value="Confuso">Confuso</option>
                                            <option value="Decepcionado">Decepcionado</option>
                                            <option value="Tonto">Tonto</option>
                                            <option value="Impaciente">Impaciente</option>
                                            <option value="Avergonzado">Avergonzado</option>
                                            <option value="Excitado">Excitado</option>
                                        </select>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1 text-sm text-green-900">¿Como pareja prefieres a:</h3>
                                        <select
                                            name="sexual_preference"
                                            id="sexual_preference"
                                            className={inputClass()}
                                            value={formData.sexual_preference}
                                            onChange={handleInputChange}
                                            disabled={!editMode}
                                        >
                                            <option value="Hombres">Hombres</option>
                                            <option value="Mujeres">Mujeres</option>
                                            <option value="Ambos">Ambos</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="pt-3 flex justify-end gap-3 text-sm">
                                {!editMode && (
                                    <button
                                        type="button"
                                        onClick={() => setEditMode(true)}
                                        className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1.5 rounded-lg font-semibold hover:bg-emerald-100"
                                    >
                                        Editar
                                    </button>
                                )}
                                {editMode && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={handleCancel}
                                            className="bg-gray-100 text-gray-700 border border-gray-200 px-3 py-1.5 rounded-lg font-semibold hover:bg-gray-200"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleFormSubmit}
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-1.5 px-4 rounded-lg"
                                        >
                                            Guardar
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
}

export default Profile;
